import clsx from 'clsx';
import { CSSProperties, forwardRef, useCallback, useMemo } from 'react';
import { MouseEvent, ReactElement, Ref } from 'react';

import globalStyle from '@vestico/shared/src/layouts/utils.module.css';

import style from './Button.module.css';
import Spinner, { SpinnerPositioning, SpinnerSize } from './Spinner';

export type ButtonStyleType = 'default' | 'plain' | 'outline';
export type ButtonSize = 'xsmall' | 'small' | 'default';
export interface ButtonProps {
  content: string | ReactElement;
  onClick: () => void;
  color?: string;
  disabled?: boolean;
  plain?: boolean;
  size?: ButtonSize;
  outline?: boolean;
  underline?: boolean;
  rounded?: boolean;
  roundedBlur?: boolean;
  dark?: boolean;
  cssProperties?: CSSProperties;
  noPadding?: boolean;
  noHover?: boolean;
  light?: boolean;
  loading?: boolean;
  ariaLabel?: string | null;
}

export const Button = forwardRef(
  (
    {
      plain,
      size,
      onClick,
      content,
      color,
      disabled,
      outline,
      rounded,
      roundedBlur,
      dark,
      underline,
      cssProperties,
      noPadding,
      noHover,
      light,
      loading,
      ariaLabel,
    }: ButtonProps,
    ref: Ref<HTMLButtonElement>
  ) => {
    const customButtonStyles: CSSProperties = useMemo(() => {
      const properties = cssProperties ?? {};

      if (color) {
        if (!plain && !outline) {
          if (!roundedBlur) {
            properties.backgroundColor = color;
          }
          properties.outlineColor = color;
          properties.borderColor = color;
        } else {
          properties.color = color;
        }
      }
      return properties;
    }, [color, cssProperties, outline, plain, roundedBlur]);

    const customSpanStyles: CSSProperties = useMemo(() => {
      const properties: CSSProperties = {};

      if (color && (plain || outline)) {
        properties.color = color;
      }
      return properties;
    }, [color, outline, plain]);

    const handleClick = useCallback(
      (e: MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        e.preventDefault();
        // e.currentTarget.blur();
        onClick();
      },
      [onClick]
    );

    return (
      <button
        type="button"
        onClick={handleClick}
        className={clsx(style.button, {
          [style.plain]: plain,
          [style.underline]: underline,
          [style.small]: size === 'small',
          [style.xsmall]: size === 'xsmall',
          [style.outline]: outline,
          [style.rounded]: rounded,
          [style.roundedBlur]: roundedBlur,
          [style.dark]: dark,
          [style.noPadding]: noPadding,
          [style.noHover]: noHover,
          [style.light]: light,
          [globalStyle.textNormal]: size === 'default',
          [globalStyle.textSmall]: size === 'small' || size === 'xsmall',
        })}
        style={customButtonStyles}
        disabled={disabled}
        aria-label={ariaLabel ?? undefined}
        ref={ref}
      >
        {loading ? (
          <div className={clsx(style.loading)}>
            <Spinner size={SpinnerSize.small} positioning={SpinnerPositioning.default} color="#FFF" secondaryColor="#FFF" />
          </div>
        ) : null}
        <span className={clsx(style.content, { [style.hidden]: loading })} style={customSpanStyles}>
          {content}
        </span>
      </button>
    );
  }
);
